import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/pages/home/components/MovieCard';
import { moviesData, categoryInfo } from '@/mocks/movies';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = useMemo(() => moviesData.find((m) => m.id === id), [id]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return moviesData
      .filter((m) => m.category === movie.category && m.id !== movie.id)
      .slice(0, 4);
  }, [movie]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-error-warning-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">
            内容未找到
          </h2>
          <p className="text-foreground-500 text-sm mb-6">该影视内容可能已下架或链接无效</p>
          <Link
            to="/"
            className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const cat = categoryInfo[movie.category];

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: cat.name, path: `/category/${movie.category}` },
    { label: movie.title },
  ];

  const movieSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.descriptionLong,
    image: movie.coverImage,
    dateCreated: movie.releaseDate,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((name) => ({
      '@type': 'Person',
      name,
    })),
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
    inLanguage: movie.language,
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(movieSchema)}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb items={breadcrumbItems} />

        <main>
          <section className="w-full px-4 md:px-6 py-6">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0">
                <div className="w-full sm:w-64 md:w-72 aspect-[2/3] rounded-xl overflow-hidden">
                  <img
                    src={movie.coverImage}
                    alt={movie.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  {movie.isHot && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50 whitespace-nowrap">
                      热播
                    </span>
                  )}
                  {movie.isNew && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
                      最新更新
                    </span>
                  )}
                  <span className="text-sm text-foreground-500">{movie.subCategory}</span>
                </div>

                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  {movie.title}
                </h1>

                <div className="flex items-center gap-4 flex-wrap mb-5">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-primary-500"></i>
                    <span className="text-lg font-semibold text-foreground-900">{movie.rating}</span>
                    <span className="text-sm text-foreground-500">/ 10</span>
                  </div>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">{movie.year}</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">{movie.region}</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">{movie.language}</span>
                </div>

                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                  {movie.descriptionLong}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 whitespace-nowrap min-w-[4rem]">导演：</span>
                      <span className="text-sm text-foreground-800">{movie.director}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 whitespace-nowrap min-w-[4rem]">主演：</span>
                      <span className="text-sm text-foreground-800">{movie.actors.join('、')}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 whitespace-nowrap min-w-[4rem]">状态：</span>
                      <span className={`text-sm font-medium ${movie.status === '连载中' ? 'text-accent-500' : 'text-foreground-800'}`}>
                        {movie.status}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 whitespace-nowrap min-w-[4rem]">集数：</span>
                      <span className="text-sm text-foreground-800">{movie.episodes}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 whitespace-nowrap min-w-[4rem]">上映：</span>
                      <span className="text-sm text-foreground-800">{movie.releaseDate}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 whitespace-nowrap min-w-[4rem]">更新：</span>
                      <span className="text-sm text-accent-500">{movie.updateInfo}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即播放
                  </button>
                  <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-share-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </section>

          {relatedMovies.length > 0 && (
            <section className="w-full px-4 md:px-6 py-6 md:py-8">
              <div className="mb-5">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} size="small" />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}